import {
  BellNotifications,
  Calendar,
  ClockWatch,
  EarthPin,
  RecordVideoCamera,
  Repeat,
  X,
} from '@griply/assets';
import { LocationSearch } from '@griply/hooks';
import {
  CalendarEventAvailability,
  CalendarEventFormFields,
  CalendarEventGuest,
  CalendarEventRef,
  CalendarEventReminder,
  CalendarRef,
  ConnectionCalendar,
  ExternalConnectionContact,
  ExternalConnectionsProviders,
  LocationSuggestion,
} from '@griply/types';
import {
  CALENDAR_EVENT_AVAILABILITY_LABEL_KEYS,
  CalendarEventRowState,
  formatLocationSuggestionLabel,
  getCalendarEventAvailabilityOptions,
  getCalendarEventRepeatDisplay,
  getConnectionCalendar,
  getTimeZoneOptions,
  ProviderRecurrenceLimits,
  setCalendarEventAllDay,
  setCalendarEventEndDate,
  setCalendarEventStartDate,
  setCalendarEventStartTime,
} from '@griply/utils';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarSelect } from 'shared/components/ui/calendar-select';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { Divider } from 'shared/components/ui/divider';
import { FormRemindersInput } from 'shared/components/ui/form-reminders-input';
import {
  FormSelectTrigger,
  Size as TriggerSize,
} from 'shared/components/ui/form-select-trigger';
import { Icon } from 'shared/components/ui/icon';
import { EventRepeatForm } from 'shared/components/ui/repeat-rrule-form';
import { Toggle } from 'shared/components/ui/toggle';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { WeekDays } from 'shared/types/week-days';
import { preventImplicitSubmit } from 'shared/utils/prevent-implicit-submit';
import { useTheme } from 'styled-components';

import * as Styled from './event-form.style';
import { EventFormHeader } from './event-form-header';
import { EventRow } from './event-row';
import { EventDateTimeRow } from './rows/event-date-time-row';
import { EventDescriptionRow } from './rows/event-description-row';
import { EventGuestsRow } from './rows/event-guests-row';
import { EventLocationRow } from './rows/event-location-row';
import { EventVideoCallRow } from './rows/event-video-call-row';

export type EventFormProps = {
  fields: CalendarEventFormFields;
  onChangeFields: (partial: Partial<CalendarEventFormFields>) => void;
  calendars: ConnectionCalendar[];
  selectableCalendars: ConnectionCalendar[];
  contacts?: ExternalConnectionContact[];
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  timeFormat: TimeFormatOptions;
  // the seeded rows are already live; these gate only the rows that need the detail fetch
  isLoadingDetails?: boolean;
  isLoadingCalendars?: boolean;
  rowState: CalendarEventRowState;
  // an instance carries no rrule of its own, so the Repeat toggle reads this, not `fields.rrule`
  isRecurring?: boolean;
  // the series master's rule, label only — the row stays read-only either way
  seriesRrule?: string | null;
  // an Outlook event that already has a video call can never remove it
  isVideoCallLocked?: boolean;
  videoCallUrl?: string | null;
  // composed by getCalendarEventVideoCall: the provider's own conference label, else the enum name
  videoCallLabel: string;
  // set only for a Google event whose conference is not already Meet — the backend refuses the swap anywhere else
  canReplaceVideoCall?: boolean;
  // the provider's original description markup, for the rendered read view
  descriptionHtml?: string | null;
  titleRef?: React.RefObject<HTMLTextAreaElement>;
  locationSearch: LocationSearch;
  // narrowed when the target calendar's provider cannot store every rule Griply can express
  recurrenceLimits?: ProviderRecurrenceLimits;
  // the availability list is provider-dependent too — Google can only store Busy and Free
  provider?: ExternalConnectionsProviders | null;
  // header actions — omitted on create, where there is nothing to act on yet
  eventTarget?: CalendarEventRef;
  isOrganizer?: boolean;
  onDuplicate?: () => void;
  onAfterAction?: () => void;
};

export const EventForm: React.FC<EventFormProps> = ({
  fields,
  onChangeFields: update,
  calendars,
  selectableCalendars,
  contacts,
  dateFormat,
  weekStartsOn,
  timeFormat,
  isLoadingDetails,
  isLoadingCalendars,
  rowState,
  isRecurring,
  seriesRrule,
  isVideoCallLocked,
  videoCallUrl,
  videoCallLabel,
  canReplaceVideoCall,
  descriptionHtml,
  titleRef,
  locationSearch,
  recurrenceLimits,
  provider,
  eventTarget,
  isOrganizer,
  onDuplicate,
  onAfterAction,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const theme = useTheme();

  const repeatTriggerRef = useRef<HTMLDivElement>(null);
  const {
    menuOpen: repeatOpen,
    openMenu: openRepeat,
    closeMenu: closeRepeat,
  } = useOpenMenu();

  const onChangeTitle = (title: string) => update({ title });
  const onChangeDescription = (description: string) => update({ description });
  // `update` takes a partial and exposes no previous state, so the post-await check below
  // reads the live value from here instead of a stale render closure.
  const locationRef = useRef(fields.location);
  locationRef.current = fields.location;

  const onChangeLocation = (location: string) => {
    update({ location });
    locationSearch.onInputChange(location);
  };

  const onSelectLocationSuggestion = async (suggestion: LocationSuggestion) => {
    const optimistic = formatLocationSuggestionLabel(suggestion);
    update({ location: optimistic });
    const resolved = await locationSearch.resolveLocation(suggestion);
    // swap the optimistic label for the resolved address only if nothing changed meanwhile
    if (!resolved || locationRef.current !== optimistic) {
      return;
    }
    update({ location: resolved });
  };

  const onToggleAllDay = () =>
    update(setCalendarEventAllDay(fields, !fields.allDay));

  const onChangeStartDate = (date: Date | null) => {
    if (!date) {
      return;
    }
    update(setCalendarEventStartDate(fields, date));
  };

  const onChangeEndDate = (date: Date | null) => {
    if (!date) {
      return;
    }
    update(setCalendarEventEndDate(fields, date));
  };

  const onChangeStartTime = (time?: number) => {
    if (time === undefined) {
      return;
    }
    update(setCalendarEventStartTime(fields, time));
  };

  const onChangeEndTime = (time?: number) => {
    if (time === undefined) {
      return;
    }
    update({ endTime: time });
  };

  const onChangeRrule = (rrule: string) => update({ rrule });

  // the toggle owns "does this repeat at all"; turning it on opens the builder so the
  // user never lands on an implicit default they did not choose
  const onToggleRepeat = () => {
    if (fields.rrule) {
      update({ rrule: null });
      return;
    }
    openRepeat();
  };

  const onCloseRepeat = () => closeRepeat();
  const onClearRepeat = () => update({ rrule: null });

  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const onEnableTimeZone = () => update({ timeZone: browserTimeZone });
  // the toggle is gone once a zone is set, so clearing the field is what drops the override
  const onChangeTimeZone = (timeZone?: string) =>
    update({ timeZone: timeZone ?? null });

  const onChangeGuests = (guests: CalendarEventGuest[]) => update({ guests });
  // Turning the call off clears any armed swap with it: there would be nothing left to replace.
  const onToggleVideoCall = () =>
    update({ videoCall: !fields.videoCall, replaceVideoCall: false });
  const onDeleteVideoCall = () =>
    update({ videoCall: false, replaceVideoCall: false });
  const onReplaceVideoCall = () => update({ replaceVideoCall: true });
  const onChangeCalendar = (calendar: CalendarRef) => update({ calendar });
  const onChangeReminders = (reminders: CalendarEventReminder[] | null) =>
    update({ reminders });

  const availabilityOptions = useMemo(
    () =>
      getCalendarEventAvailabilityOptions(provider).map((availability) => ({
        label: t(CALENDAR_EVENT_AVAILABILITY_LABEL_KEYS[availability]),
        value: availability,
      })),
    [t, provider],
  );
  const onChangeAvailability = (value?: string) =>
    update({
      availability:
        (value as CalendarEventAvailability) ?? CalendarEventAvailability.Busy,
    });

  const timeZoneOptions = useMemo(
    () => getTimeZoneOptions([fields.timeZone, browserTimeZone]),
    [browserTimeZone, fields.timeZone],
  );

  // the master's pattern is shown but never edited — an edit would recur this occurrence alone
  const {
    repeats,
    isSeriesPatternUnknown,
    label: repeatLabel,
  } = useMemo(
    () =>
      getCalendarEventRepeatDisplay({
        rrule: fields.rrule,
        seriesRrule,
        isRecurring,
        today,
        t,
        weekStartsOn,
      }),
    [fields.rrule, seriesRrule, isRecurring, t, today, weekStartsOn],
  );

  // an occurrence's rule belongs to the master, so that one reads rather than reopening the builder
  const showRepeatTrigger = !!repeatLabel && !isSeriesPatternUnknown;
  const showRepeatPattern = !!repeatLabel && isSeriesPatternUnknown;
  // also covers a rule this build cannot name — the toggle is then the only way to drop it
  const showRepeatToggle = !repeatLabel;

  const selectedCalendar = useMemo(
    () => getConnectionCalendar(calendars, fields.calendar),
    [calendars, fields.calendar],
  );

  // the video-call row follows the selected calendar's capability; a locked
  // Outlook video call keeps the row visible so the disabled state is explained
  const supportsVideoCall =
    !!isVideoCallLocked || !!selectedCalendar?.supportsVideoCall;

  // `videoCall: false` is a pending removal the save will apply, so the link stops standing in for one
  const hasLinkedVideoCall = !!videoCallUrl && fields.videoCall;

  // a locked Outlook call keeps its disabled toggle — that is what explains why it cannot be removed
  const showVideoCallToggle =
    supportsVideoCall && (!hasLinkedVideoCall || !!isVideoCallLocked);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) =>
    event.preventDefault();

  return (
    <Styled.Form
      noValidate
      onSubmit={onSubmit}
      onKeyDown={preventImplicitSubmit}
    >
      <EventFormHeader
        value={fields.title}
        onChange={onChangeTitle}
        titleRef={titleRef}
        eventTarget={eventTarget}
        isRecurring={isRecurring}
        isOrganizer={isOrganizer}
        onDuplicate={onDuplicate}
        onAfterAction={onAfterAction}
      />

      <Divider noMargin />
      <Styled.ScrollArea>
        <Styled.Body>
          <Styled.Section>
            <EventRow
              icon={ClockWatch}
              label={t('forms.event.fields.all-day.label')}
              autoWidthField
            >
              <Styled.ToggleRow>
                <Toggle
                  value="allDay"
                  checked={fields.allDay}
                  onChange={onToggleAllDay}
                />
              </Styled.ToggleRow>
            </EventRow>

            <EventDateTimeRow
              label={t('forms.event.fields.starts.label')}
              date={fields.startDate}
              time={fields.startTime}
              allDay={fields.allDay}
              today={today}
              weekStartsOn={weekStartsOn}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
              timePlaceholder={t('forms.event.fields.starts.placeholder')}
              onChangeDate={onChangeStartDate}
              onChangeTime={onChangeStartTime}
            />

            <EventDateTimeRow
              label={t('forms.event.fields.ends.label')}
              date={fields.endDate}
              time={fields.endTime}
              allDay={fields.allDay}
              today={today}
              weekStartsOn={weekStartsOn}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
              timePlaceholder={t('forms.event.fields.ends.placeholder')}
              minDate={fields.startDate}
              onChangeDate={onChangeEndDate}
              onChangeTime={onChangeEndTime}
            />

            <EventRow
              icon={Repeat}
              label={t('forms.event.fields.repeat.label')}
              autoWidthField={showRepeatToggle}
              isLoading={isLoadingDetails}
              skeletonWidth="4rem"
            >
              <Styled.TriggerAnchor
                ref={repeatTriggerRef}
                $fullWidth={showRepeatTrigger}
              >
                {showRepeatTrigger && (
                  <FormSelectTrigger
                    size={TriggerSize.ExtraSmall}
                    onClick={openRepeat}
                    ariaHasPopup="dialog"
                    ariaExpanded={repeatOpen}
                    noOverflow
                    endAdornment={
                      <Styled.ClearButton
                        onClick={onClearRepeat}
                        type="button"
                        tabIndex={-1}
                        aria-label={t('general.clear')}
                      >
                        <Icon
                          icon={X}
                          size={16}
                          strokeWidth={2}
                          color={theme.palette.grey[600]}
                        />
                      </Styled.ClearButton>
                    }
                  >
                    {repeatLabel}
                  </FormSelectTrigger>
                )}

                {showRepeatPattern && (
                  <Styled.ReadOnlyValue>{repeatLabel}</Styled.ReadOnlyValue>
                )}

                {showRepeatToggle && (
                  <Styled.ToggleRow>
                    <Toggle
                      value="repeat"
                      checked={repeats}
                      onChange={onToggleRepeat}
                      disabled={isSeriesPatternUnknown}
                    />
                  </Styled.ToggleRow>
                )}

                {repeatOpen && (
                  <EventRepeatForm
                    initialRrule={fields.rrule}
                    weekStartsOn={weekStartsOn}
                    onChange={onChangeRrule}
                    onClose={onCloseRepeat}
                    referenceElement={repeatTriggerRef}
                    limits={recurrenceLimits}
                  />
                )}
              </Styled.TriggerAnchor>
            </EventRow>

            <EventRow
              icon={EarthPin}
              label={t('forms.event.fields.time-zone.label')}
              autoWidthField={!fields.timeZone}
              // the toggle is the "add a zone" affordance and is replaced by the
              // field itself once there is one — which is only known after the
              // target calendar's own zone has loaded
              isLoading={isLoadingDetails || isLoadingCalendars}
              skeletonWidth="4rem"
            >
              {fields.timeZone ? (
                <CustomFormSelect
                  value={fields.timeZone}
                  options={timeZoneOptions}
                  onChange={onChangeTimeZone}
                  size={SelectSize.ExtraSmall}
                  searchPlaceholder={t(
                    'forms.event.fields.time-zone.search-placeholder',
                  )}
                  canClear
                />
              ) : (
                <Styled.ToggleRow>
                  <Toggle
                    value="timeZone"
                    checked={false}
                    onChange={onEnableTimeZone}
                  />
                </Styled.ToggleRow>
              )}
            </EventRow>
          </Styled.Section>
        </Styled.Body>

        <Divider noMargin />

        <Styled.Body>
          <Styled.Section>
            <EventGuestsRow
              value={fields.guests}
              contacts={contacts}
              isLoading={rowState.isLoadingGuests}
              onChange={onChangeGuests}
            />

            {showVideoCallToggle && (
              <EventRow
                icon={RecordVideoCamera}
                autoWidthField
                alignTop
                isLoading={rowState.isLoadingVideoCall}
                skeletonWidth="4rem"
              >
                <Styled.ToggleRow>
                  <Toggle
                    value="videoCall"
                    checked={fields.videoCall}
                    onChange={onToggleVideoCall}
                    disabled={isVideoCallLocked}
                  />
                </Styled.ToggleRow>
              </EventRow>
            )}

            {hasLinkedVideoCall && (
              <EventVideoCallRow
                url={videoCallUrl}
                label={videoCallLabel}
                icon={showVideoCallToggle ? undefined : RecordVideoCamera}
                // the only way to change WHICH call, offered only where the backend honours the swap
                onReplace={canReplaceVideoCall ? onReplaceVideoCall : undefined}
                isReplacePending={fields.replaceVideoCall}
                // Outlook cannot drop a conference it minted, so that one keeps its toggle instead
                onDelete={isVideoCallLocked ? undefined : onDeleteVideoCall}
              />
            )}

            <EventLocationRow
              value={fields.location}
              suggestions={locationSearch.suggestions}
              isSearching={locationSearch.isSearching}
              isLoading={rowState.isLoadingLocation}
              onChange={onChangeLocation}
              onSelectSuggestion={onSelectLocationSuggestion}
            />
          </Styled.Section>
        </Styled.Body>

        <Divider noMargin />

        <Styled.Body>
          <Styled.Section>
            <EventDescriptionRow
              value={fields.description}
              html={descriptionHtml}
              isLoading={rowState.isLoadingDescription}
              onChange={onChangeDescription}
            />
          </Styled.Section>
        </Styled.Body>
      </Styled.ScrollArea>
      <Divider noMargin />

      <Styled.Body>
        <Styled.Section>
          <EventRow
            icon={Calendar}
            startSlot={
              selectedCalendar && (
                <Styled.CalendarDot $color={selectedCalendar.color} />
              )
            }
            isLoading={isLoadingCalendars || isLoadingDetails}
          >
            <Styled.CalendarFields>
              <CalendarSelect
                calendars={calendars}
                selectableCalendars={selectableCalendars}
                value={fields.calendar}
                onChange={onChangeCalendar}
              />
              <CustomFormSelect
                value={fields.availability}
                options={availabilityOptions}
                onChange={onChangeAvailability}
                size={SelectSize.ExtraSmall}
              />
            </Styled.CalendarFields>
          </EventRow>

          <EventRow icon={BellNotifications} isLoading={isLoadingDetails}>
            <FormRemindersInput
              value={fields.reminders}
              onChange={onChangeReminders}
            />
          </EventRow>
        </Styled.Section>
      </Styled.Body>
    </Styled.Form>
  );
};
